/**
 * Push в браузере сотрудника (R-117, docs/notes/admin-notifications.md).
 *
 * Три дела: получить токен Firebase и закрепить браузер за вошедшим
 * сотрудником (сразу после входа — только если разрешение уже дано, иначе по
 * кнопке), отвязать браузер перед выходом и передать странице push, пришедший,
 * пока вкладка на виду. Firebase грузится лениво — в основной чанк он не
 * попадает. Service worker — `public/firebase-messaging-sw.js`, без SDK.
 */
import type { ApiClient } from '@dorixona/api'
import { env, type FirebaseEnv } from '../../config/env'
import type { AdminAuthApi } from '../auth/api'
import { registerPushToken } from './api'

/** Какой токен закрепили: при выходе отвязываем ровно его. */
export const PUSH_TOKEN_KEY = 'dorixona.admin.pushToken'

/** Дольше выход не ждёт отвязки браузера. */
export const PUSH_UNREGISTER_TIMEOUT = 3000

/** Service worker лежит в корне сайта: иначе его область не накроет страницы панели. */
export const PUSH_SW_URL = '/firebase-messaging-sw.js'

/** Так service worker отдаёт вкладке push, который пришёл, пока она на виду. */
export const STAFF_PUSH_MESSAGE = 'dorixona-staff-push'

/**
 * Что с push в этом браузере:
 * - `unsupported` — браузер не умеет web push;
 * - `unconfigured` — в сборке нет ключей Firebase;
 * - `off` — разрешение не спрашивали или браузер ещё не закреплён;
 * - `denied` — уведомления запрещены в настройках сайта;
 * - `enabled` — разрешено и браузер закреплён за сотрудником.
 */
export type PushStatus = 'unsupported' | 'unconfigured' | 'off' | 'denied' | 'enabled'

function stored(): string | null {
  try {
    return window.localStorage.getItem(PUSH_TOKEN_KEY)
  } catch {
    return null
  }
}

const listeners = new Set<() => void>()

function store(token: string | null) {
  try {
    if (token === null) window.localStorage.removeItem(PUSH_TOKEN_KEY)
    else window.localStorage.setItem(PUSH_TOKEN_KEY, token)
  } catch {
    // Хранилище закрыто — при выходе отвязывать будет нечего; сервер сам
    // забудет токен, когда Firebase перестанет его принимать.
  }
  listeners.forEach((listener) => {
    listener()
  })
}

/** Браузер умеет web push: уведомления, service worker и PushManager. */
export function browserSupportsPush(): boolean {
  return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window
}

export function pushStatus(): PushStatus {
  if (!browserSupportsPush()) return 'unsupported'
  if (!env.firebase) return 'unconfigured'
  if (Notification.permission === 'denied') return 'denied'
  return Notification.permission === 'granted' && stored() ? 'enabled' : 'off'
}

/**
 * Подписка на смену состояния push — для `useSyncExternalStore`. Разрешение
 * меняют и в настройках сайта, поэтому перечитываем и при возврате во вкладку.
 */
export function subscribePush(listener: () => void): () => void {
  listeners.add(listener)
  window.addEventListener('focus', listener)
  return () => {
    listeners.delete(listener)
    window.removeEventListener('focus', listener)
  }
}

/** Токен браузера от Firebase; null — Firebase говорит, что браузер не подходит. */
async function firebaseToken(config: FirebaseEnv): Promise<string | null> {
  const [{ getApps, initializeApp }, messaging] = await Promise.all([
    import('firebase/app'),
    import('firebase/messaging'),
  ])
  if (!(await messaging.isSupported())) return null
  const { vapidKey, ...options } = config
  const app = getApps()[0] ?? initializeApp(options)
  const registration = await navigator.serviceWorker.register(PUSH_SW_URL)
  return messaging.getToken(messaging.getMessaging(app), {
    vapidKey,
    serviceWorkerRegistration: registration,
  })
}

/**
 * Включить push: при `ask` спросить разрешение (только по нажатию — сами не
 * спрашиваем), получить токен и закрепить браузер. Без `ask` работает, только
 * если разрешение уже дано. Сбой сети или Firebase — исключение.
 */
export async function enablePush(api: ApiClient, ask: boolean): Promise<PushStatus> {
  const config = env.firebase
  if (!browserSupportsPush()) return 'unsupported'
  if (!config) return 'unconfigured'
  // Разрешение спрашиваем первым делом: браузер показывает окно, только пока
  // нажатие свежее, а загрузка Firebase может занять время.
  const permission = ask ? await Notification.requestPermission() : Notification.permission
  if (permission !== 'granted') {
    store(stored())
    return permission === 'denied' ? 'denied' : 'off'
  }
  const token = await firebaseToken(config)
  if (!token) return 'unsupported'
  await registerPushToken(api, token)
  store(token)
  return 'enabled'
}

/**
 * Отвязать браузер перед выходом — тем токеном, что закрепляли, и с токеном
 * доступа уходящей сессии. Не дольше PUSH_UNREGISTER_TIMEOUT и без отказов.
 */
export async function unregisterPush(
  api: Pick<AdminAuthApi, 'unregisterPushToken'>,
  accessToken: string,
): Promise<void> {
  const token = stored()
  if (!token) return
  store(null)
  let timer = 0
  try {
    await Promise.race([
      api.unregisterPushToken(token, accessToken),
      new Promise((resolve) => {
        timer = window.setTimeout(resolve, PUSH_UNREGISTER_TIMEOUT)
      }),
    ])
  } catch (error) {
    console.warn('[push] unregister failed', error)
  } finally {
    window.clearTimeout(timer)
  }
}

function isStaffPush(data: unknown): boolean {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as { type?: unknown }).type === STAFF_PUSH_MESSAGE
  )
}

/**
 * Push, пришедший при открытой вкладке: service worker не показывает его
 * системным окном, а передаёт сюда. Возвращает отписку.
 */
export function listenStaffPush(onPush: () => void): () => void {
  if (!('serviceWorker' in navigator)) return () => undefined
  const container = navigator.serviceWorker
  const onMessage = (event: MessageEvent<unknown>) => {
    if (isStaffPush(event.data)) onPush()
  }
  container.addEventListener('message', onMessage)
  // С addEventListener очередь сообщений сама не открывается.
  container.startMessages()
  return () => {
    container.removeEventListener('message', onMessage)
  }
}
